import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api, mediaUrl } from '@/lib/api';
import { Download, CheckCircle2, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { COLLECT } from '@/constants/testIds';

export default function CheckoutSuccessPage() {
  const location = useLocation();
  const [status, setStatus] = useState('checking');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) { setError('Missing session id.'); setStatus('error'); return; }

    let timer = null;
    const poll = async () => {
      if (attemptsRef.current >= 8) { setStatus('timeout'); return; }
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setData(data);
        if (data.payment_status === 'paid') { setStatus('paid'); return; }
        if (data.status === 'expired') { setStatus('expired'); return; }
        timer = setTimeout(poll, 2000);
      } catch (e) { setError(e?.response?.data?.detail || 'Could not verify payment.'); setStatus('error'); }
    };
    poll();
    return () => { if (timer) clearTimeout(timer); };
  }, [location.search]);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-16 pb-24">
      <motion.div initial={{ opacity: 1, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="pg-card p-8 sm:p-10 text-center" style={{ borderColor: 'rgba(255,62,165,0.25)' }}>
        {status === 'checking' && (<>
          <p className="pg-text-glow-emerald font-mono text-[11px] tracking-[0.32em]">CONFIRMING</p>
          <h1 className="font-display text-3xl mt-3 tracking-[0.04em]">A MOMENT, PLEASE…</h1>
          <p className="text-[var(--pg-ink-muted)] mt-3">We're verifying with Stripe. This usually takes a few seconds.</p>
        </>)}
        {status === 'paid' && (
          <div data-testid={COLLECT.checkoutSuccess}>
            <div className="flex justify-center text-[var(--pg-neon-pink)]"><CheckCircle2 size={42} /></div>
            <p className="pg-text-glow-pink font-mono text-[11px] tracking-[0.32em] mt-4">THANK YOU</p>
            <h1 className="font-display text-3xl sm:text-4xl mt-2 tracking-[0.04em]">WELCOME TO {data?.product_title?.toUpperCase() || 'YOUR PURCHASE'}.</h1>
            <p className="text-[var(--pg-ink-muted)] mt-3">Your payment has been received. Save this page — your download link sits below.</p>
            {data?.download_url ? (
              <a data-testid={COLLECT.downloadLink} href={mediaUrl(data.download_url)} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 pg-btn-neon">
                <Download size={16} /> DOWNLOAD
              </a>
            ) : (
              <p className="text-sm text-[var(--pg-ink-muted)] mt-6">A link will be added to this product shortly. We'll send you the download by email.</p>
            )}
            <div className="pg-hairline my-8" />
            <Link to="/collect" className="inline-flex items-center gap-2 text-sm text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)]">Back to the Vault <ArrowRight size={14} /></Link>
          </div>
        )}
        {status === 'expired' && (<>
          <p className="font-mono text-[11px] tracking-[0.32em] pg-text-glow-pink">SESSION EXPIRED</p>
          <h1 className="font-display text-3xl mt-2 tracking-[0.04em]">THE SESSION CLOSED.</h1>
          <Link to="/collect" className="mt-6 inline-block pg-btn-ghost">Try again</Link>
        </>)}
        {status === 'timeout' && (<>
          <p className="font-mono text-[11px] tracking-[0.32em] pg-text-glow-emerald">STILL PROCESSING</p>
          <h1 className="font-display text-3xl mt-2 tracking-[0.04em]">THIS IS TAKING LONGER THAN USUAL.</h1>
          <p className="text-[var(--pg-ink-muted)] mt-3">If Stripe confirms the payment, this page will refresh. Otherwise, please contact us.</p>
        </>)}
        {status === 'error' && (<>
          <p className="font-mono text-[11px] tracking-[0.32em] pg-text-glow-pink">SOMETHING WENT WRONG</p>
          <h1 className="font-display text-3xl mt-2 tracking-[0.04em]">WE COULDN'T VERIFY YOUR PURCHASE.</h1>
          <p className="text-[var(--pg-ink-muted)] mt-3">{error}</p>
          <Link to="/collect" className="mt-6 inline-block pg-btn-ghost">Back to Collect</Link>
        </>)}
      </motion.div>
    </div>
  );
}
